import { useMemo } from "react"
import { Box, Table, TableBody, TableCell, TableContainer, TableRow, Divider, Typography } from "@mui/material"
import { useTranslation } from "react-i18next"

export default function VehicleStats({ events = [] }) {
    const { t } = useTranslation()

    const { rows, totals } = useMemo(() => {
        const counts = new Map() // key -> { key, label, entry, exit }
        
        const getLabel = (event) => {
            const subtype = event?.vehicleSubtype?.name
            if (subtype) return t(`pages.access.vehicle.subtypes.${subtype}`, subtype)

            const type = event?.vehicleType?.name
            if (type) return t(`pages.access.vehicle.types.${type}`, type)

            return t("pages.access.stats.unknown")
        }

        const getKey = (event) => (
            event?.vehicleSubtype?.name
                ? `sub:${event.vehicleSubtype.name}`
                : event?.vehicleType?.name
                    ? `type:${event.vehicleType.name}`
                    : "unknown"
        )

        for (const elem of events) {
            const key = getKey(elem)
            const item = counts.get(key) ?? { key, label: getLabel(elem), entry: 0, exit: 0 }
            const direction = elem?.direction

            if (direction === "entry") item.entry += 1
            else if (direction === "exit") item.exit += 1
            else item.entry += 1 // fallback: unknown direction -> counts once

            counts.set(key, item)
        }

        const computedRows = Array.from(counts.values())
            .sort((a, b) => {
                const at = a.entry + a.exit
                const bt = b.entry + b.exit
                return bt - at || a.label.localeCompare(b.label)
            })

        const totals = computedRows.reduce(
            (acc, current) => ({
                entry: acc.entry + current.entry,
                exit: acc.exit + current.exit
            }),
            { entry: 0, exit: 0 }
        )

        return { rows: computedRows, totals }
    }, [events, t])

    return (
        <Box sx={{ mt: 2 }}>

            <Divider sx={{ userSelect: "none" }}>
                <Typography>
                    {t("pages.access.vehicle.title")}
                </Typography>
            </Divider>

            <TableContainer
                sx={{ mt: 1 }}
                className="thin-scrollbar"
            >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                    <TableBody>

                        <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                            <TableCell sx={{ pl: "2px", pr: 0 }} />
                            <TableCell align="right" sx={{ px: 0 }}>{t("pages.access.entry")}</TableCell>
                            <TableCell align="right" sx={{ px: 0 }}>{t("pages.access.exit")}</TableCell>
                        </TableRow>

                        <TableRow sx={{ "& td": { fontWeight: 600 } }}>
                            <TableCell sx={{ pl: "2px", pr: 0 }}>{t("download.total")}</TableCell>
                            <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{totals.entry}</TableCell>
                            <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{totals.exit}</TableCell>
                        </TableRow>

                        {rows.map((elem) => (
                            <TableRow key={elem.key} sx={{ "&:last-child td": { border: 0 } }}>
                                <TableCell sx={{ pl: "2px", pr: 0 }}>{elem.label}</TableCell>
                                <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{elem.entry}</TableCell>
                                <TableCell align="right" sx={{ pr: "4px", pl: 0 }}>{elem.exit}</TableCell>
                            </TableRow>
                        ))}

                    </TableBody>
                </Table>
            </TableContainer>
            
        </Box>
    )
}